import axios from 'axios';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance - always use credentials for cookie-based auth
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to ensure credentials are always sent
api.interceptors.request.use(
  (config) => {
    // Always include credentials
    config.withCredentials = true;
    
    // Add authorization header if token exists in sessionStorage (fallback for cookies)
    const token = sessionStorage.getItem('auth_token');
    if (token && !config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${token}`;
    } else if (!token) {
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only log non-auth errors (suppress 401 on /auth/me for public visitors)
    if (error.response && error.response.status !== 401) {
      console.error('API Error:', error.response.status);
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  processSession: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

// Settings API
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (settings) => api.put('/settings', settings),
};

// Solutions API
export const solutionsAPI = {
  getAll: () => api.get('/solutions'),
  getBySlug: (slug) => api.get(`/solutions/${slug}`),
  create: (solution) => api.post('/solutions', solution),
  update: (id, solution) => api.put(`/solutions/${id}`, solution),
  delete: (id) => api.delete(`/solutions/${id}`),
};

// Team API
export const teamAPI = {
  getAll: () => api.get('/team'),
  create: (member) => api.post('/team', member),
  update: (id, member) => api.put(`/team/${id}`, member),
  delete: (id) => api.delete(`/team/${id}`),
};

// Insights API
export const insightsAPI = {
  getAll: () => api.get('/insights'),
  getAllAdmin: () => api.get('/admin/insights'),
  getBySlug: (slug, previewToken) => api.get(`/insights/${slug}`, previewToken ? { params: { preview: previewToken } } : undefined),
  create: (insight) => api.post('/insights', insight),
  update: (id, insight) => api.put(`/insights/${id}`, insight),
  delete: (id) => api.delete(`/insights/${id}`),
};

// Testimonials API
export const testimonialsAPI = {
  getAll: () => api.get('/testimonials'),
  create: (testimonial) => api.post('/testimonials', testimonial),
  update: (id, testimonial) => api.put(`/testimonials/${id}`, testimonial),
  delete: (id) => api.delete(`/testimonials/${id}`),
};

// Case Studies API
export const paymentsAPI = {
  getAll: (status) => api.get('/admin/payments', status ? { params: { status } } : undefined),
  resendLink: (workshopId, registrationId, amountCents, expiryDays = 7) =>
    api.post(`/admin/workshops/${workshopId}/registrations/${registrationId}/send-sentoo-link`, { amount_cents: amountCents, expiry_days: expiryDays }),
};

export const casesAPI = {
  getAll: () => api.get('/cases'),
  getAllAdmin: () => api.get('/admin/cases'),
  getBySlug: (slug, previewToken) => api.get(`/cases/${slug}`, previewToken ? { params: { preview: previewToken } } : undefined),
  create: (caseStudy) => api.post('/cases', caseStudy),
  update: (id, caseStudy) => api.put(`/cases/${id}`, caseStudy),
  delete: (id) => api.delete(`/cases/${id}`),
};

// Values API
export const valuesAPI = {
  getAll: () => api.get('/values'),
  update: (values) => api.put('/values', values),
};

// Newsletter API
export const newsletterAPI = {
  subscribe: (data) => api.post('/newsletter/subscribe', data),
  getSubscribers: () => api.get('/newsletter/subscribers'),
};

// Admin Stats API
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  seed: () => api.post('/seed'),
};

// Page Headers API
export const pageHeadersAPI = {
  getAll: () => api.get('/page-headers'),
  getBySlug: (slug) => api.get(`/page-headers/${slug}`),
  update: (slug, data) => api.put(`/page-headers/${slug}`, data),
};

// Games API
export const gamesAPI = {
  getAll: () => api.get('/games'),
  getAllAdmin: () => api.get('/admin/games'),
  getBySlug: (slug) => api.get(`/games/${slug}`),
  getCategories: () => api.get('/games/categories'),
  getEventTypes: () => api.get('/games/event-types'),
  filter: (params) => api.get('/games/filter', { params }),
  create: (game) => api.post('/games', game),
  update: (id, game) => api.put(`/games/${id}`, game),
  delete: (id) => api.delete(`/games/${id}`),
};

// Client Logos API
export const clientLogosAPI = {
  getAll: () => api.get('/client-logos'),
  getAllAdmin: () => api.get('/admin/client-logos'),
  create: (logo) => api.post('/client-logos', logo),
  update: (id, logo) => api.put(`/client-logos/${id}`, logo),
  delete: (id) => api.delete(`/client-logos/${id}`),
};

// Workshops API
export const workshopsAPI = {
  getAll: () => api.get('/workshops'),
  getUpcoming: () => api.get('/workshops/upcoming'),
  getAllAdmin: () => api.get('/admin/workshops'),
  getBySlug: (slug) => api.get(`/workshops/${slug}`),
  create: (workshop) => api.post('/workshops', workshop),
  update: (id, workshop) => api.put(`/workshops/${id}`, workshop),
  delete: (id) => api.delete(`/workshops/${id}`),
  register: (id, data) => api.post(`/workshops/${id}/register`, data),
  getRegistrations: (id) => api.get(`/admin/workshops/${id}/registrations`),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/admin/users'),
  update: (id, data) => api.put(`/admin/users/${id}`, data),
  delete: (id) => api.delete(`/admin/users/${id}`),
};

// Contact API
export const contactAPI = {
  submit: (data) => api.post('/contact', data),
};

// Marketing Popup API
export const popupAPI = {
  getActive: () => api.get('/popup/active'),
  getConfig: () => api.get('/admin/popup'),
  updateConfig: (data) => api.put('/admin/popup', data),
  subscribeNewsletter: (email) => api.post('/popup/newsletter', { email }),
};

export default api;
